/* eslint-disable sort-imports */
/**
 * Upload review — the mapping panels: sheet work centres → the company's,
 * a job role per work centre (SAP recipes carry hours, not crews), and the
 * job-order PDF's phase codes → job roles.
 */
import { Box, Chip, FormControl, Grid2, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, Typography } from "@mui/material";
import React, { useCallback } from "react";

import { MonoText, planner,SectionLabel } from "../../planner-ui";
import { DistinctPhase, formatHours,MasterJob } from "../utils/recipe-mapping";
import { chipMonoSx, fieldSx, menuItemSx, panelSx, statusChipSx } from "./upload-styles";

// ---------------------------------------------------------------------------
// Sheet work centres the company does not have
// ---------------------------------------------------------------------------

const WorkcenterMappingRow: React.FC<{
	raw: string;
	value: string;
	options: string[];
	onChange: (raw: string, value: string) => void;
}> = ({ raw, value, options, onChange }) => {
	const handleChange = useCallback((e: SelectChangeEvent<string>) => onChange(raw, e.target.value), [onChange, raw]);
	return (
		<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
			<FormControl fullWidth size="small" sx={fieldSx}>
				<InputLabel>{raw}</InputLabel>
				<Select value={value} label={raw} onChange={handleChange}>
					<MenuItem value="">
						<em>Not mapped</em>
					</MenuItem>
					{options.map((wc) => (
						<MenuItem key={wc} value={wc}>
							{wc}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</Grid2>
	);
};

export const WorkcenterMapping: React.FC<{
	unknownWorkstations: string[];
	unresolvedCount: number;
	workstationMap: Record<string, string>;
	options: string[];
	onChange: (raw: string, value: string) => void;
}> = ({ unknownWorkstations, unresolvedCount, workstationMap, options, onChange }) => (
	<Paper elevation={0} sx={{ ...panelSx, mb: 2 }}>
		<Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
			<SectionLabel>Work centres to map</SectionLabel>
			<Chip
				label={unresolvedCount === 0 ? "All mapped" : `${unresolvedCount} unmapped`}
				size="small"
				sx={
					unresolvedCount === 0
						? statusChipSx(planner.staffed, planner.staffedSoft)
						: statusChipSx(planner.partial, planner.partialSoft)
				}
			/>
		</Box>
		<Typography sx={{ ...planner.type.body, fontFamily: planner.fontUi, color: planner.inkSecondary, mt: 0.5, mb: 1.5 }}>
			The sheet names resources Wilya does not know. Pick the work centre each one is, so every step lands on a real line.
		</Typography>
		<Grid2 container spacing={1.5}>
			{unknownWorkstations.map((raw) => (
				<WorkcenterMappingRow key={raw} raw={raw} value={workstationMap[raw] || ""} options={options} onChange={onChange} />
			))}
		</Grid2>
	</Paper>
);

// ---------------------------------------------------------------------------
// One job role per work centre
// ---------------------------------------------------------------------------

const RoleMappingRow: React.FC<{
	name: string;
	value: number | "";
	jobRoles: MasterJob[];
	onChange: (name: string, roleId: number | "") => void;
}> = ({ name, value, jobRoles, onChange }) => {
	const handleChange = useCallback(
		(e: SelectChangeEvent<number | "">) => onChange(name, (e.target.value as number | "") || ""),
		[onChange, name]
	);
	return (
		<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
			<FormControl fullWidth size="small" sx={fieldSx}>
				<InputLabel>{name}</InputLabel>
				<Select value={value} label={name} onChange={handleChange}>
					<MenuItem value="">
						<em>Pick a job role</em>
					</MenuItem>
					{jobRoles.map((jr) => (
						<MenuItem key={jr.id} value={jr.id}>
							{jr.name}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</Grid2>
	);
};

export const RoleMapping: React.FC<{
	workstations: string[];
	unmappedCount: number;
	workstationRoles: Record<string, number | "">;
	jobRoles: MasterJob[];
	isSap: boolean;
	onChange: (name: string, roleId: number | "") => void;
}> = ({ workstations, unmappedCount, workstationRoles, jobRoles, isSap, onChange }) => (
	<Paper elevation={0} sx={{ ...panelSx, mb: 2 }}>
		<Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
			<SectionLabel>Job role per work centre</SectionLabel>
			<Chip
				label={unmappedCount === 0 ? "All mapped" : `${unmappedCount} to pick`}
				size="small"
				sx={
					unmappedCount === 0
						? statusChipSx(planner.staffed, planner.staffedSoft)
						: statusChipSx(planner.partial, planner.partialSoft)
				}
			/>
		</Box>
		<Typography sx={{ ...planner.type.body, fontFamily: planner.fontUi, color: planner.inkSecondary, mt: 0.5, mb: 1.5 }}>
			{isSap
				? "The SAP recipe gives hours per resource but no crew. Pick who staffs each work centre; the phases on it take that role."
				: "Some phases have no job role in the sheet. Pick who staffs each work centre; those phases take that role."}
		</Typography>
		<Grid2 container spacing={1.5}>
			{workstations.map((name) => (
				<RoleMappingRow key={name} name={name} value={workstationRoles[name] ?? ""} jobRoles={jobRoles} onChange={onChange} />
			))}
		</Grid2>
	</Paper>
);

// ---------------------------------------------------------------------------
// Job-order PDF: document phases → job roles
// ---------------------------------------------------------------------------

const PhaseRoleRow: React.FC<{
	phase: DistinctPhase;
	suggestion: string;
	chosenRole: string;
	jobRoles: MasterJob[];
	onChange: (phaseCode: string, role: string) => void;
}> = ({ phase, suggestion, chosenRole, jobRoles, onChange }) => {
	const isAutoMapped = Boolean(suggestion) && chosenRole === suggestion;
	const isOverridden = Boolean(suggestion) && Boolean(chosenRole) && chosenRole !== suggestion;
	const handleChange = useCallback(
		(event: SelectChangeEvent<string>) => onChange(phase.phaseCode, event.target.value),
		[onChange, phase.phaseCode]
	);
	return (
		<Box display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ py: 1.25, borderTop: `1px solid ${planner.hairline}` }}>
			<Chip label={phase.phaseCode} size="small" sx={{ ...chipMonoSx, minWidth: 64 }} />
			<Box sx={{ minWidth: 180, flex: "1 1 220px" }}>
				<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
					<Typography sx={{ ...planner.type.bodyStrong, fontFamily: planner.fontUi, color: planner.ink }}>
						{phase.description || "(no description)"}
					</Typography>
					<Chip
						label={
							isAutoMapped
								? "auto-mapped"
								: isOverridden
									? "overridden"
									: phase.workPhaseId !== null
										? "no default role"
										: "not configured"
						}
						size="small"
						sx={
							isAutoMapped
								? statusChipSx(planner.staffed, planner.staffedSoft)
								: phase.workPhaseId !== null
									? statusChipSx(planner.partial, planner.partialSoft)
									: statusChipSx(planner.unplanned, planner.unplannedSoft)
						}
					/>
				</Box>
				<MonoText sx={{ color: planner.inkMuted, fontSize: 11 }}>
					{`${phase.occurrences}× · ${formatHours(phase.totalHours)}`}
				</MonoText>
				{/* What the system knows about this code, in one quiet line. No
				    link away — the user is mid-import. */}
				{phase.workPhaseId !== null ? (
					<Typography sx={{ ...planner.type.meta, fontFamily: planner.fontUi, color: planner.inkMuted, mt: 0.25 }}>
						{"Your work phase "}
						<Box component="span" sx={{ fontFamily: planner.fontMono, color: planner.accentDeep }}>
							{phase.phaseCode}
						</Box>
						{phase.workPhaseName ? ` ${phase.workPhaseName}` : ""}
						{isAutoMapped
							? " set this role."
							: isOverridden
								? ` suggests ${suggestion} — you changed it.`
								: " has no default job role yet, so this role is a guess."}
					</Typography>
				) : (
					<Typography sx={{ ...planner.type.meta, fontFamily: planner.fontUi, color: planner.inkMuted, mt: 0.25 }}>
						{"Not one of your work phases. Add "}
						<Box component="span" sx={{ fontFamily: planner.fontMono, color: planner.ink }}>
							{phase.phaseCode}
						</Box>
						{" under Setup → Work phases with a default job role and the next import maps it for you."}
					</Typography>
				)}
			</Box>
			<FormControl size="small" sx={{ ...fieldSx, minWidth: 240 }}>
				<InputLabel id={`phase-role-${phase.phaseCode}`}>Job role</InputLabel>
				<Select labelId={`phase-role-${phase.phaseCode}`} label="Job role" value={chosenRole} onChange={handleChange}>
					{jobRoles.map((role) => (
						<MenuItem key={role.id} value={role.name} sx={menuItemSx}>
							{role.name}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</Box>
	);
};

export const PdfPhaseMapping: React.FC<{
	distinctPhases: DistinctPhase[];
	phaseRoles: Record<string, string>;
	workPhaseRole: (phase: DistinctPhase) => string;
	unmappedCount: number;
	autoMappedCount: number;
	allAutoMapped: boolean;
	jobRoles: MasterJob[];
	onChange: (phaseCode: string, role: string) => void;
}> = ({ distinctPhases, phaseRoles, workPhaseRole, unmappedCount, autoMappedCount, allAutoMapped, jobRoles, onChange }) => (
	<Paper elevation={0} sx={{ ...panelSx, mb: 2 }}>
		<Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
			<SectionLabel>{allAutoMapped ? "Phases mapped from your work phases" : "Document phases and job roles"}</SectionLabel>
			<Chip
				label={
					unmappedCount > 0
						? `${unmappedCount} need a role`
						: allAutoMapped
							? `all ${distinctPhases.length} auto-mapped`
							: `${autoMappedCount} of ${distinctPhases.length} auto-mapped`
				}
				size="small"
				sx={
					unmappedCount > 0
						? statusChipSx(planner.partial, planner.partialSoft)
						: autoMappedCount > 0
							? statusChipSx(planner.staffed, planner.staffedSoft)
							: statusChipSx(planner.unplanned, planner.unplannedSoft)
				}
			/>
		</Box>
		<Typography sx={{ ...planner.type.body, fontFamily: planner.fontUi, color: planner.inkSecondary, mt: 0.75, mb: 1.5 }}>
			{allAutoMapped
				? "Every phase code in this document is configured under Setup → Work phases, so each one arrived with its job role. Nothing to do here — override any of them if this job order is an exception."
				: autoMappedCount > 0
					? `${autoMappedCount} of ${distinctPhases.length} phase codes are configured under Setup → Work phases and came in with their job role. Pick a role for the rest.`
					: "None of these phase codes are configured under Setup → Work phases yet, so the roles below are guessed from the descriptions — check each one."}
		</Typography>

		{distinctPhases.map((phase) => (
			<PhaseRoleRow
				key={phase.phaseCode}
				phase={phase}
				suggestion={workPhaseRole(phase)}
				chosenRole={phaseRoles[phase.phaseCode] || ""}
				jobRoles={jobRoles}
				onChange={onChange}
			/>
		))}
	</Paper>
);
